import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Plus,
  Heart,
  Star,
  Award,
  ArrowLeft,
  Sparkles,
  HandHeart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ErrorModal from "../Modal/ErrorModal";
import FetchErrorState from "../Common/FetchErrorState";
import {
  getFollowers,
  getFollowing,
  toggleFollow,
} from "../../services/followService";
import { getAdmirers } from "../../services/admireService";

const StatsModal = ({ isOpen, onClose, initialTab, profileData }) => {
  const navigate = useNavigate();
  const { apiCall, user: loggedInUser } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || "followers");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [followingStates, setFollowingStates] = useState({});
  const [followingLoading, setFollowingLoading] = useState({});
  const [fetchError, setFetchError] = useState(null);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const abortControllerRef = useRef(null);
  const observerTarget = useRef(null);

  const handleProfileClick = (username) => {
    navigate(`/${username}`);
    onClose();
  };

  const getProfilePicture = (profilePic) => {
    if (profilePic && typeof profilePic === "object") {
      return profilePic.thumbnail || profilePic.small || profilePic.medium;
    }

    if (typeof profilePic === "string") {
      return profilePic;
    }

    return null;
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const admireIcons = {
    inspiration: { icon: Star },
    idol: { icon: Sparkles },
    adore: { icon: Heart },
    support: { icon: HandHeart },
  };

  // Update activeTab when modal opens or initialTab changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || "followers");
    }
  }, [isOpen, initialTab]);

  // Reset state when tab changes
  useEffect(() => {
    if (isOpen) {
      setData([]);
      setCursor(null);
      setHasMore(true);
      setFollowingStates({});
      fetchData(true);
    }
  }, [activeTab, isOpen]);

  // Fetch data function
  const fetchData = useCallback(
    async (reset = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      if (!reset && (!hasMore || loading)) return;

      setLoading(true);
      try {
        let response;
        const currentCursor = reset ? null : cursor;

        // Fetch based on the active tab
        switch (activeTab) {
          case "followers":
            response = await getFollowers(
              apiCall,
              profileData.profileID,
              30,
              currentCursor,
            );
            break;
          case "following":
            response = await getFollowing(
              apiCall,
              profileData.profileID,
              30,
              currentCursor,
            );
            break;
          case "admirers":
            response = await getAdmirers(
              apiCall,
              profileData.profileID,
              30,
              currentCursor,
            );
            break;
          default:
            return;
        }

        if (signal.aborted) {
          return;
        }

        const newData = response[activeTab] || response.data || [];

        const newFollowingStates = {};
        newData.forEach((item) => {
          newFollowingStates[item._id] =
            item.followStatus?.isFollowing || false;
        });

        setData((prev) => (reset ? newData : [...prev, ...newData]));
        setFollowingStates((prev) => ({ ...prev, ...newFollowingStates }));
        setCursor(response.nextCursor);
        setHasMore(response.hasMore);
      } catch (error) {
        if (error.name !== "AbortError") {
          if (reset) {
            setFetchError("We couldn't load this list.");
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [activeTab, apiCall, profileData.profileID, hasMore, loading, cursor],
  );

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchData();
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, fetchData, isOpen]);

  const handleFollowToggle = async (targetProfileId) => {
    if (followingLoading[targetProfileId]) return;

    setFollowingLoading((prev) => ({ ...prev, [targetProfileId]: true }));

    try {
      const result = await toggleFollow(apiCall, targetProfileId);

      // Update the specific item in the data array
      setData((prevData) =>
        prevData.map((item) =>
          item._id === targetProfileId
            ? {
                ...item,
                followStatus: {
                  isFollowing: result.data.isFollowing,
                  isAccepted: result.data.isAccepted || 0,
                },
              }
            : item,
        ),
      );

      setFollowingStates((prev) => ({
        ...prev,
        [targetProfileId]: result.data.isFollowing,
      }));
    } catch (error) {
      setErrorModal({
        isOpen: true,
        message: "We couldn't process this follow request. Please try again.",
      });
    } finally {
      setFollowingLoading((prev) => ({ ...prev, [targetProfileId]: false }));
    }
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  if (!isOpen) return null;

  // Mobile layout
  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Mobile Header with Back Arrow */}
        <div className="border-b border-gray-200 px-4 py-3">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors mr-3"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900">
              @{profileData?.username}
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex justify-center items-center gap-4 mt-3">
            <button
              onClick={() => setActiveTab("followers")}
              className={`pb-3 px-2 text-sm font-semibold transition-colors relative ${
                activeTab === "followers"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Followers
              {activeTab === "followers" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("following")}
              className={`pb-3 px-2 text-sm font-semibold transition-colors relative ${
                activeTab === "following"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Following
              {activeTab === "following" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("admirers")}
              className={`pb-3 px-2 text-sm font-semibold transition-colors relative ${
                activeTab === "admirers"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Admirers
              {activeTab === "admirers" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && data.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : fetchError && data.length === 0 ? (
            <FetchErrorState
              message={fetchError}
              onRetry={() => fetchData(true)}
            />
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-gray-500 text-sm">
                {activeTab === "followers" && "No followers yet"}
                {activeTab === "following" && "Not following anyone yet"}
                {activeTab === "admirers" && "No admirers yet"}
              </p>
            </div>
          ) : (
            <div className="py-2">
              {data.map((item) => {
                const isCurrentUser =
                  item._id === loggedInUser?.profile?.profileID;
                const isFollowing = followingStates[item._id];
                const isLoading = followingLoading[item._id];

                return (
                  <div
                    key={item._id}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    <div
                      onClick={() => handleProfileClick(item.username)}
                      className="flex items-center cursor-pointer gap-3 flex-1 min-w-0"
                    >
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 p-[2px] flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white p-[1px] overflow-hidden">
                          {(() => {
                            const profilePictureUrl = getProfilePicture(
                              item.profilePicture,
                            );
                            return profilePictureUrl ? (
                              <img
                                src={profilePictureUrl}
                                alt={item.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                                <span className="text-slate-600 font-semibold text-sm">
                                  {getInitials(item.name)}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {item.username}
                        </p>
                      </div>
                    </div>

                    {/* Right: Action Button/Icon */}
                    {activeTab === "admirers" ? (
                      // Show admire type icon for admirers tab
                      <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-gray-300 bg-white flex-shrink-0">
                        {React.createElement(
                          admireIcons[item.admireType]?.icon || Heart,
                          {
                            className: "w-5 h-5 text-gray-900",
                          },
                        )}
                      </div>
                    ) : (
                      // Show Follow/Following/Requested button for followers and following tabs
                      !item.isCurrentUser && (
                        <button
                          onClick={() => handleFollowToggle(item._id)}
                          disabled={isLoading}
                          className={`
            px-3 py-1.5 rounded-full text-sm font-semibold 
            transition-all duration-200 flex items-center gap-1.5 flex-shrink-0
            ${
              isFollowing
                ? item.followStatus?.isAccepted === 1
                  ? "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                : "bg-[#b0d284] text-black hover:bg-[#9ec170]"
            }
            ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
        `}
                        >
                          {isLoading ? (
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isFollowing ? (
                            <>
                              {item.followStatus?.isAccepted === 1 ? (
                                <span>Following</span>
                              ) : (
                                <span>Requested</span>
                              )}
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              <span>Follow</span>
                            </>
                          )}
                        </button>
                      )
                    )}
                  </div>
                );
              })}

              {/* Loading indicator for infinite scroll */}
              {loading && data.length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              )}

              {/* Observer target for infinite scroll */}
              <div ref={observerTarget} className="h-4" />
            </div>
          )}
        </div>
        <ErrorModal
          isOpen={errorModal.isOpen}
          message={errorModal.message}
          onClose={() => setErrorModal({ isOpen: false, message: "" })}
        />
      </div>
    );
  }

  // Desktop/Layout layout
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl w-full max-w-md flex flex-col shadow-2xl"
        style={{ height: "480px" }}
      >
        {/* Header */}
        <div className="px-4 pt-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              @{profileData?.username}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("followers")}
              className={`pb-3 px-2 text-sm font-semibold transition-colors relative ${
                activeTab === "followers"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Followers
              {activeTab === "followers" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("following")}
              className={`pb-3 px-2 text-sm font-semibold transition-colors relative ${
                activeTab === "following"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Following
              {activeTab === "following" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("admirers")}
              className={`pb-3 px-2 text-sm font-semibold transition-colors relative ${
                activeTab === "admirers"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Admirers
              {activeTab === "admirers" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
              )}
            </button>
          </div>
        </div>

        {/* Content - Fixed height with scrolling */}
        <div className="flex-1 overflow-y-auto">
          {loading && data.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : fetchError && data.length === 0 ? (
            <FetchErrorState
              message={fetchError}
              onRetry={() => fetchData(true)}
            />
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-gray-500 text-sm">
                {activeTab === "followers" && "No followers yet"}
                {activeTab === "following" && "Not following anyone yet"}
                {activeTab === "admirers" && "No admirers yet"}
              </p>
            </div>
          ) : (
            <div className="py-2">
              {data.map((item) => {
                const isCurrentUser =
                  item._id === loggedInUser?.profile?.profileID;
                const isFollowing = followingStates[item._id];
                const isLoading = followingLoading[item._id];

                return (
                  <div
                    key={item._id}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    {/* Left: Profile Picture + Username */}
                    <div
                      onClick={() => handleProfileClick(item.username)}
                      className="flex items-center cursor-pointer gap-3 flex-1 min-w-0"
                    >
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 p-[2px] flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white p-[1px] overflow-hidden">
                          {(() => {
                            const profilePictureUrl = getProfilePicture(
                              item.profilePicture,
                            );
                            return profilePictureUrl ? (
                              <img
                                src={profilePictureUrl}
                                alt={item.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                                <span className="text-slate-600 font-semibold text-sm">
                                  {getInitials(item.name)}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {item.username}
                        </p>
                      </div>
                    </div>

                    {/* Right: Action Button/Icon */}
                    {activeTab === "admirers" ? (
                      // Show admire type icon for admirers tab
                      <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-gray-300 bg-white flex-shrink-0">
                        {React.createElement(
                          admireIcons[item.admireType]?.icon || Heart,
                          {
                            className: "w-5 h-5 text-gray-900",
                          },
                        )}
                      </div>
                    ) : (
                      // Show Follow/Following/Requested button for followers and following tabs
                      !item.isCurrentUser && (
                        <button
                          onClick={() => handleFollowToggle(item._id)}
                          disabled={isLoading}
                          className={`
            px-3 py-1.5 rounded-full text-sm font-semibold 
            transition-all duration-200 flex items-center gap-1.5 flex-shrink-0
            ${
              isFollowing
                ? item.followStatus?.isAccepted === 1
                  ? "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                : "bg-[#b0d284] text-black hover:bg-[#9ec170]"
            }
            ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
        `}
                        >
                          {isLoading ? (
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isFollowing ? (
                            <>
                              {item.followStatus?.isAccepted === 1 ? (
                                <span>Following</span>
                              ) : (
                                <span>Requested</span>
                              )}
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              <span>Follow</span>
                            </>
                          )}
                        </button>
                      )
                    )}
                  </div>
                );
              })}

              {/* Loading indicator for infinite scroll */}
              {loading && data.length > 0 && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              )}

              {/* Observer target for infinite scroll */}
              <div ref={observerTarget} className="h-4" />
            </div>
          )}
        </div>

        {/* Footer - Close Button */}
        <div className="border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 text-base sm:text-lg text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default StatsModal;
